import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router';
import useSweepNavigate from '../../navigation-utils/useSweepNavigate';
import useQueryParams from '../../navigation-utils/useQueryParams';
import usePrevious from '../../hooks/usePrevious';
import { SettingSection } from './agentSettings/AgentSettingsDialog';

interface AgentSettingsContextState {
  isSettingsOpen: boolean;
  initialSection: SettingSection | undefined;
  setIsSettingsOpen: (isOpen: boolean) => void;
  setInitialSection: (section: SettingSection | undefined) => void;
}

const AgentSettingsContext = createContext<AgentSettingsContextState | undefined>(undefined);

interface AgentSettingsProviderProps {
  children: ReactNode;
}

export const AgentSettingsProvider = ({ children }: AgentSettingsProviderProps) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [initialSection, setInitialSection] = useState<SettingSection | undefined>();

  const contextValue: AgentSettingsContextState = {
    isSettingsOpen,
    initialSection,
    setIsSettingsOpen,
    setInitialSection,
  };

  return (
    <AgentSettingsContext.Provider value={contextValue}>
      {children}
    </AgentSettingsContext.Provider>
  );
};

export const useAgentSettingsContext = () => {
  const context = useContext(AgentSettingsContext);
  if (!context) {
    throw new Error('useAgentSettingsContext must be used within an AgentSettingsProvider');
  }

  const { isSettingsOpen, initialSection, setIsSettingsOpen, setInitialSection } = context;
  const { navigate, removeQueryParams } = useSweepNavigate();
  const location = useLocation();
  const queryParams = useQueryParams();
  const hasSettingsParam = queryParams.has('settings');
  const prevHasSettingsParam = usePrevious(hasSettingsParam);

  const openSettings = useCallback((section?: SettingSection) => {
    setInitialSection(section);
    setIsSettingsOpen(true);
  }, [setInitialSection, setIsSettingsOpen]);

  const closeSettings = useCallback(() => {
    setIsSettingsOpen(false);
    setInitialSection(undefined);
  }, [setIsSettingsOpen, setInitialSection]);

  // Handle opening settings from URL parameter
  useEffect(() => {
    if (hasSettingsParam && !prevHasSettingsParam && !isSettingsOpen) {
      openSettings(SettingSection.Instructions);
    }
  }, [hasSettingsParam, prevHasSettingsParam, isSettingsOpen, openSettings]);

  // Sync URL parameter with settings state
  useEffect(() => {
    if (isSettingsOpen) {
      const currentSearchParams = new URLSearchParams(location.search);
      currentSearchParams.set('settings', '');
      navigate(`${location.pathname}?${currentSearchParams.toString()}`, { replace: true });
    } else {
      // Only remove the settings param if it actually exists to avoid unnecessary navigation
      const currentSearchParams = new URLSearchParams(location.search);
      if (currentSearchParams.has('settings')) {
        removeQueryParams(['settings']);
      }
    }
    // Intentionally excluding location.search and location.pathname to avoid re-running when URL changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSettingsOpen, navigate, removeQueryParams]);

  return {
    isSettingsOpen,
    initialSection,
    openSettings,
    closeSettings,
  };
};
